import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { apiGet, apiPost, apiDelete } from '../utils/api';
import { useUser } from './UserContext';

const FavoritesContext = createContext();

export function FavoritesProvider({ children }) {
  const { user, loading } = useUser();
  const [favorites, setFavorites] = useState([]);
  const [loadedOwner, setLoadedOwner] = useState(null);
  const [favoritesLoading, setFavoritesLoading] = useState(false); // 즐겨찾기 리스트 API 로딩 상태

  const ownerRef = useRef(user?.idx);
  ownerRef.current = user?.idx;

  // 즐겨찾기 리스트를 불러오는 함수
  const fetchFavorites = async () => {
    const requestedOwner = user?.idx;
    if (requestedOwner !== ownerRef.current) return;
    if (!user) {
      setFavorites([]);
      setFavoritesLoading(false);
      return;
    }
    try {
      setFavoritesLoading(true); // API 호출 시작
      const res = await apiGet('/member/favorites.php');
      if (requestedOwner !== ownerRef.current) return;
      setLoadedOwner(requestedOwner);
      if (res?.result === 'success' && Array.isArray(res.data)) {
        setFavorites([...res.data]);
      } else {
        setFavorites([]);
        console.error('즐겨찾기 데이터를 불러오지 못했습니다.', res);
      }
    } catch (e) {
      if (requestedOwner !== ownerRef.current) return;
      setFavorites([]);
      console.error('즐겨찾기 불러오기 실패:', e);
    } finally {
      if (requestedOwner === ownerRef.current) setFavoritesLoading(false); // Ignore the previous account response.
    }
  };

  useEffect(() => {
    if (user && !loading) {
      fetchFavorites();
    } else {
      setFavorites([]);
      setFavoritesLoading(false); // 사용자 정보 없으면 로딩도 초기화
    }
  }, [user, loading]);

  // 즐겨찾기 여부 확인
  const visibleFavorites = loadedOwner === user?.idx ? favorites : [];
  const isFavorite = (id) => visibleFavorites.some(fav => String(fav.id) === String(id));

  // 즐겨찾기 추가: 서버 응답 그대로 반환
  const addFavorite = async (id) => {
    const res = await apiPost('/member/favorites.php', { cruise_schedules_idx: id });
    if (res?.result === 'success') {
      await fetchFavorites();
    }
    return res; // 서버에서 내려주는 전체 응답 반환
  };

  // 즐겨찾기 삭제: 서버 응답 그대로 반환
  const removeFavorite = async (id) => {
    const res = await apiDelete('/member/favorites.php', { cruise_schedules_idx: id });
    if (res?.result === 'success') {
      await fetchFavorites();
    }
    return res; // 서버에서 내려주는 전체 응답 반환
  };

  return (
    <FavoritesContext.Provider
      value={{ favorites: visibleFavorites, favoritesLoading, isFavorite, addFavorite, removeFavorite }}
    >
      {children}
    </FavoritesContext.Provider>
  );
}

export const useFavorites = () => useContext(FavoritesContext);
